import { Container, Grid, Typography, Button, LinearProgress, Alert, AlertTitle, Box } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BasicUserData from './componets/BasicDataUser';
import BillingDetails from './componets/BillingDetails';
import ContactData from './componets/ContactData';
import { useEffect, useMemo, useState } from 'react';
import { ContactDetails, dictionaryService, FormDataPerson, Token, sanitizeFormData } from './services/dictionaryService';
import Statements from './componets/StatementsData';
import SettlementData from './componets/SettlementData';
import Agreement from './componets/Agreement';
import SmsVerificationDialog from './componets/SmsVerificationDialog';
import { phoneRegex, getIbanError } from './helpers/validation';
import { getBankNameFromIban } from './helpers/iban';
import { TranslateFn, useLanguage } from './i18n/LanguageContext';

// Sekcja oświadczeń ZUS (pola TAK/NIE). Jedna flaga steruje trzema rzeczami naraz:
// reguły walidacji dopinane do schematu, wysyłka pola `declaration` oraz widoczność sekcji.
// Ustawienie na `false` wyłącza całość spójnie — formularz nie zablokuje się wtedy na polach
// wymaganych, których nikt nie widzi, i nie wyśle oświadczeń, których nikt nie złożył.
// Rachunek bankowy jest od tej flagi niezależny i zostaje zawsze.
const ZUS_STATEMENTS_ENABLED = true;

// Reguły walidacji pól z sekcji oświadczeń ZUS. Trzymane osobno i dopinane do schematu
// tylko gdy sekcja jest widoczna — inaczej formularz byłby nie do wysłania przez pola
// wymagane, których nikt nie widzi.
const zusStatementsValidation = (t: TranslateFn) => ({
    socialInsuranceFromOtherContract: Yup.boolean().required(t('v.required')),
    contractOfMandateWithOtherEntity: Yup.boolean().required(t('v.required')),
    salaryAboveMinimum: Yup.boolean().when('socialInsuranceFromOtherContract', {
        is: true,
        then: schema => schema.required(t('v.required')),
        otherwise: schema => schema.notRequired(),
    }),
    mandate_salaryAboveMinimum: Yup.boolean().when('contractOfMandateWithOtherEntity', {
        is: true,
        then: schema => schema.required(t('v.required')),
        otherwise: schema => schema.notRequired(),
    }),
    periodFrom: Yup.date()
        .nullable()
        .when('contractOfMandateWithOtherEntity', {
            is: true,
            then: schema => schema.required(t('v.startDateRequired')),
            otherwise: schema => schema.notRequired(),
        }),
    periodTo: Yup.date()
        .nullable()
        .when('contractOfMandateWithOtherEntity', {
            is: true,
            then: schema => schema.required(t('v.endDateRequired')),
            otherwise: schema => schema.notRequired(),
        }),
    entityName: Yup.string()
        .max(100, t('v.nameMax100'))
        .when(['socialInsuranceFromOtherContract'], {
            is: (insurance: boolean) => insurance === true,
            then: schema => schema.required(t('v.entityNameRequired')),
            otherwise: schema => schema.notRequired(),
        }),
    nameAndAddress: Yup.string()
        .max(100, t('v.nameMax100'))
        .when(['contractOfMandateWithOtherEntity'], {
            is: (insurance: boolean) => insurance === true,
            then: schema => schema.required(t('v.entityNameRequired')),
            otherwise: schema => schema.notRequired(),
        }),
    retired: Yup.boolean().required(t('v.required')),
    pensioner: Yup.boolean().required(t('v.required')),
    student: Yup.boolean().required(t('v.required')),
    studentIdNumber: Yup.string().when('student', {
        is: true,
        then: schema => schema.required(t('v.studentIdRequired')),
        otherwise: schema => schema.notRequired(),
    }),
    studentIdScan: Yup.mixed().when('student', {
        is: true,
        then: schema => schema.required(t('v.studentIdScanRequired')),
        otherwise: schema => schema.notRequired(),
    }),
    disability: Yup.boolean().required(t('v.required')),
    degree: Yup.string().when('disability', {
        is: true,
        then: schema => schema.required(t('v.degreeRequired')),
        otherwise: schema => schema.notRequired(),
    }),
    voluntaryHealthInsurance: Yup.boolean().required(t('v.required')),
    businessActivity: Yup.boolean().required(t('v.required')),
});

const buildValidationSchema = (t: TranslateFn) => Yup.object({
    ...(ZUS_STATEMENTS_ENABLED ? zusStatementsValidation(t) : {}),
    province: Yup.string().required(t('v.voivodeshipRequired')),
    district: Yup.string().required(t('v.countyRequired')),
    commune: Yup.string().required(t('v.municipalityRequired')),
    accountNumber: Yup.string()
        .required(t('iban.required'))
        // createError, żeby pokazać konkretną przyczynę zamiast jednego ogólnego komunikatu
        .test('is-valid-iban', '', function (value) {
            const error = getIbanError(value ?? '');
            return error ? this.createError({ message: t(error.key, error.params) }) : true;
        }),
    pesel: Yup.string()
        .required(t('v.peselRequired'))
        .matches(/^\d{11}$/, t('v.pesel11'))
        .test('is-valid-pesel', t('v.peselInvalid'), value => {
            if (!value) return false;
            const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
            const sum = value
                .split('')
                .slice(0, 10)
                .reduce((acc, digit, index) => acc + parseInt(digit, 10) * weights[index], 0);
            const controlDigit = (10 - (sum % 10)) % 10;
            return controlDigit === parseInt(value[10], 10);
        }),
    citizenship: Yup.array()
        .of(Yup.string())
        .min(1, t('v.citizenshipMin'))
        .required(t('v.citizenshipRequired')),
    taxOffice: Yup.string().required(t('v.taxOfficeRequired')),
    firstName: Yup.string().required(t('v.firstNameRequired')),
    lastName: Yup.string().required(t('v.lastNameRequired')),
    email: Yup.string().email(t('v.emailInvalid')).required(t('v.emailRequired')),
    phoneNo: Yup.string()
        .required(t('v.phoneRequired'))
        .matches(phoneRegex, t('v.phoneInvalid')),
    street: Yup.string().required(t('v.streetRequired')),
    postalCode: Yup.string()
        .required(t('v.postalRequired'))
        .matches(/^\d{2}-\d{3}$/, t('v.postalFormat')),
    city: Yup.string().required(t('v.cityRequired')),
    houseNumber: Yup.string()
        .required(t('v.houseRequired'))
        .max(6, t('v.houseMax')),
    dataAgreement: Yup.boolean().oneOf([true], t('v.required')),
    personalDataProcessingConsent: Yup.boolean().oneOf([true], t('v.required')),
    apartmentNumber: Yup.string()
        .nullable()
        .max(6, t('v.aptMax')),
});

export interface FormValues {
    firstName: string;
    lastName: string;
    email: string;
    pesel: string;
    dateOfBirth: Date | null;
    periodFrom: Date | null;
    periodTo: Date | null;
    taxOffice: string;
    taxOfficeDict: {
        value: string;
        label: string;
    };
    citizenship: string[];
    phoneNo: string;
    province: string; //WOJEWODZTWO
    district: string; //POWIAT
    commune: string; //GMINA
    city: string;
    postalCode: string;
    street: string;
    houseNumber: string;
    apartmentNumber: string;
    socialInsuranceFromOtherContract: boolean | undefined;
    contractOfMandateWithOtherEntity: boolean | undefined;
    nameAndAddress: string
    entityName: string;
    salaryAboveMinimum: boolean | undefined;
    mandate_salaryAboveMinimum: boolean | undefined;
    retired: boolean | undefined;
    pensioner: boolean | undefined;
    student: boolean | undefined;
    studentIdNumber: string;
    studentIdScan: File | null;
    disability: boolean | undefined;
    degree: string;
    voluntaryHealthInsurance: boolean | undefined,
    businessActivity: boolean | undefined,
    accountNumber: string
    dataAgreement: boolean | undefined;
    personalDataProcessingConsent: boolean | undefined;
}

const FormPagePerson = ({ tokenAll, contactDetails }: { tokenAll: Token, contactDetails: ContactDetails }) => {

    const { lang, t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [send, setSend] = useState(false);
    // Dane przygotowane do wysyłki czekają na podpis kodem SMS; zapis następuje dopiero z dowodem.
    const [pendingData, setPendingData] = useState<FormDataPerson | null>(null);
    const [attachmentError, setAttachmentError] = useState<string | null>(null);

    // Schemat zależy od języka — komunikaty walidacji muszą wyjść w wybranym języku.
    const validationSchema = useMemo(() => buildValidationSchema(t), [t]);

    const formik = useFormik<FormValues>({
        initialValues: {
            dataAgreement: false,
            personalDataProcessingConsent: false,
            firstName: '',
            lastName: '',
            email: '',
            phoneNo: "",
            pesel: "",
            dateOfBirth: null,
            periodFrom: null,
            periodTo: null,
            taxOffice: "",
            taxOfficeDict: {
              value: '',
              label: ''
            },
            citizenship: ['Polska'],
            province: "",
            district: "",
            commune: "",
            city: "",
            postalCode: "",
            street: "",
            houseNumber: "",
            apartmentNumber: "",
            contractOfMandateWithOtherEntity: undefined,
            socialInsuranceFromOtherContract: undefined,
            nameAndAddress: "",
            entityName: "",
            mandate_salaryAboveMinimum: undefined,
            salaryAboveMinimum: undefined,
            retired: undefined,
            pensioner: undefined,
            student: undefined,
            studentIdNumber: "",
            studentIdScan: null,
            disability: undefined,
            degree: "",
            voluntaryHealthInsurance: undefined,
            businessActivity: undefined,
            accountNumber: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            if (!tokenAll) return;
            const preparedData: FormDataPerson = {
                formType: "PERSONAL",
                status: "SUBMITTED",
                language: lang,
                consents: {
                    dataAgreement: values.dataAgreement === true,
                    personalDataProcessingConsent: values.personalDataProcessingConsent === true,
                },
                contactDetails: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    phoneNo: values.phoneNo,
                },
                token: tokenAll,
                personalData: {
                    lastName: values.lastName,
                    firstName: values.firstName,
                    middleName: "", // Assuming no middle name field in the form
                    pesel: values.pesel,
                    dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString().split('T')[0] : "",
                    citizenship: values.citizenship,
                    taxOfficeDict: values.taxOfficeDict,
                    taxOffice: values.taxOfficeDict.label || values.taxOffice,
                },
                residentialAddress: {
                    country: "Polska", // Assuming the country is Poland
                    voivodeship: values.province,
                    county: values.district,
                    municipality: values.commune,
                    street: values.street,
                    houseNumber: values.houseNumber,
                    apartmentNumber: values.apartmentNumber || undefined,
                    postalCode: values.postalCode,
                    city: values.city,
                },
                // Gdy sekcja jest wyłączona, nie wysyłamy pustego `declaration` z samymi
                // false — to byłyby oświadczenia, których pracownik nigdy nie złożył.
                declaration: !ZUS_STATEMENTS_ENABLED ? undefined : {
                    socialInsuranceFromOtherContract: values.socialInsuranceFromOtherContract
                        ? {
                            entityName: values.entityName,
                            salaryAboveMinimum: values.salaryAboveMinimum || false,
                        }
                        : undefined,
                    contractOfMandateWithOtherEntity: values.contractOfMandateWithOtherEntity
                        ? {
                            nameAndAddress: values.nameAndAddress,
                            salaryAboveMinimum: values.mandate_salaryAboveMinimum || false,
                            periodFrom: values.periodFrom
                                ? new Date(values.periodFrom).toISOString()
                                : "",
                            periodTo: values.periodTo
                                ? new Date(values.periodTo).toISOString()
                                : "",
                        }
                        : undefined,
                    retired: values.retired || false,
                    pensioner: values.pensioner || false,
                    student: values.student || false,
                    disability: values.disability
                        ? {
                            haveDisability: true,
                            degree: values.degree as "lekki" | "umiarkowany" | "znaczny",
                        }
                        : undefined,
                    voluntaryHealthInsurance: values.voluntaryHealthInsurance || false,
                    businessActivity: values.businessActivity || false,
                },
                settlementData: {
                    accountNumber: values.accountNumber,
                    bankName: getBankNameFromIban(values.accountNumber) ?? '',
                },
                declarationFooter: {
                    date: new Date().toISOString(),
                },
            };
            // Załącznik idzie osobnym żądaniem, zanim poprosimy o kod SMS. Kolejność jest
            // istotna: dowód z SMS-a jest jednorazowy, więc gdyby wysyłka pliku odbiła się
            // po weryfikacji, wypełniający musiałby prosić o nowy kod.
            const skan = values.studentIdScan as File | null;
            if (values.student && skan) {
                setAttachmentError(null);
                setLoading(true);
                try {
                    await dictionaryService.uploadStatementAttachment(tokenAll!.value, 'STUDENT_ID', skan);
                } catch (err) {
                    setAttachmentError(t('v.studentIdScanUploadFailed'));
                    return;
                } finally {
                    setLoading(false);
                }
            }

            // Otwiera dialog SMS — właściwy zapis wykonuje handleSigned po weryfikacji kodu.
            setPendingData(sanitizeFormData(preparedData));
        },

    }

    );

    const handleSigned = async (proof: string) => {
        if (!tokenAll || !pendingData) return;
        setLoading(true);
        try {
            await dictionaryService.postFormPerson(tokenAll.value, pendingData, proof);
            setPendingData(null);
            setSend(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        formik.setValues((prevValues) => ({
            ...prevValues,
            firstName: contactDetails.firstName,
            lastName: contactDetails.lastName,
            email: contactDetails.email,
            phoneNo: contactDetails.phoneNo,
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenAll, contactDetails])

    // Po zmianie języka już wyświetlone komunikaty walidacji mają wyjść w nowym języku.
    useEffect(() => {
        if (formik.submitCount > 0) formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);


    useEffect(() => {
        if (!formik.values.socialInsuranceFromOtherContract) {
            formik.setFieldValue('entityName', '', false);
            formik.setFieldValue('salaryAboveMinimum', null, false);
        }
        if (!formik.values.contractOfMandateWithOtherEntity) {
            formik.setFieldValue('nameAndAddress', '', false);
            formik.setFieldValue('periodFrom', null, false);
            formik.setFieldValue('periodTo', null, false);
            formik.setFieldValue('mandate_salaryAboveMinimum', null, false);
        }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        formik.values.socialInsuranceFromOtherContract,
        formik.values.contractOfMandateWithOtherEntity
    ]);

    useEffect(() => {
        if (!formik.values.disability) {
            formik.setFieldValue('degree', '', false);
        }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.disability]);

    return (
        <>
            <Container maxWidth="lg">
                {send ? (
                    <Alert severity="success" sx={{ marginTop: '32px' }}>
                        <AlertTitle>{t('form.sent.title')}</AlertTitle>
                        {t('form.sent.body')}
                    </Alert>
                ) : (
                    <form onSubmit={formik.handleSubmit} >
                        <Box sx={{
                            mt: 10, mb: 4,
                            borderRadius: '8px',
                            padding: '24px',
                            fontSize: '24px',
                            fontFamily: '"Poppins", sans-serif', // Set font family to Poppins
                            backgroundColor: "#E0F2F1",
                            border: '1px solid #00796B',
                            marginTop: { xs: "24px", md: "32px" },
                            paddingLeft:
                                { xs: "15px", lg: "24px" },
                            paddingRight: "12px"
                        }}>
                            <Typography variant="h5" gutterBottom>
                                {t('form.intro.hello')}
                            </Typography>
                            <Typography variant="body1" gutterBottom >
                                {t('form.intro.line1')}
                                <br />
                                <br />
                                {t('form.intro.line2a')}<span style={{ fontWeight: "600" }}>{t('form.intro.line2bold')}</span>{t('form.intro.line2b')}
                                <br />

                            </Typography>
                        </Box>
                        <BasicUserData<FormValues> formik={formik} />
                        <BillingDetails formik={formik} />
                        <ContactData formik={formik} />
                        {ZUS_STATEMENTS_ENABLED && (
                            <>
                                <Typography variant="h6" sx={{ marginTop: { xs: '80px', md: '130px' }, paddingBottom: { sx: '10px', md: '30px' } }}>
                                    {t('section.zusStatements')}
                                </Typography>
                                <Statements formik={formik} />
                            </>
                        )}
                        <Typography variant="h6" sx={{ marginTop: { xs: '80px', md: '130px' }, paddingBottom: { sx: '10px', md: '30px' } }}>
                            {t('section.bankAccount')}
                        </Typography>
                        <SettlementData formik={formik} />
                        <Agreement formik={formik} />
                        {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
                            <Alert severity="error" sx={{ marginTop: 2 }}>
                                {t('form.errorsSummary')}
                            </Alert>
                        )}
                        {attachmentError && (
                            <Alert severity="error" sx={{ marginTop: 2 }}>
                                {attachmentError}
                            </Alert>
                        )}
                        <Grid container justifyContent="center" sx={{ marginTop: Object.keys(formik.errors).length > 0 ? 5 : 10, marginBottom: 10, paddingBottom: '200px' }}>
                            {loading ? (
                                <LinearProgress sx={{ width: '100%' }} />
                            ) : (
                                <Button variant="contained" type="submit" color="primary" sx={{ backgroundColor: '#00796B', '&:hover': { backgroundColor: '#005A4F' } }}>
                                    {t('form.submit')}
                                </Button>
                            )}
                        </Grid>
                    </form>
                )}
            </Container>

            <SmsVerificationDialog
                open={pendingData !== null}
                token={tokenAll.value}
                onSigned={handleSigned}
                onClose={() => setPendingData(null)}
            />
        </>
    );
};

export default FormPagePerson;
